import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { addCompany, getWpfCompanies, importFromGus } from 'app/actions/company'
import { getUserInfo } from 'app/actions/user'
import { CompanyType } from 'app/constants/companyType'
import { CompanyDetailsModel, ImportTypes } from 'app/models/companyDetailsModel'
import {
  CreateCompanyModel,
  UserRoleModel,
} from 'app/models/createCompanyModel'
import { CompanyConfigurationModel } from 'app/models/company/companyConfigurationModel'
import { PredefinedPackage } from 'app/models/predefinedPackageModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { ServerModel } from 'app/models/serverModel'
import { UserModel } from 'app/models/userModel'
import { UserRole } from 'app/models/role'
import { getPredefinedPackageName } from 'app/common/converters/company/predefinedPackageToTextConverter'
import { validateEmail, validateNip } from 'app/common/validationHelper'
import { isAdmin, saveUserInfo } from 'app/services/local/roleService'


import { ConnectionStatus } from 'app/constants/connectionStatus'
import { ROLES_KEY, fetchRoles } from 'app/containers/administration/roleQueries'
import { fetchServers, serversKey } from 'app/containers/administration/serverQueries'
import { fetchUsers, usersKey } from 'app/containers/administration/userQueries'

import { Dropdown } from '@/app/features/company-selection/Dropdown'
import { RolePicker } from '../../users/components/RolePicker'
import { CheckboxField } from '@/modules/shared/components'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'
import { Button } from '@/ui/button'
import { TextField } from '@/ui/form/controls/TextField'
import { NumberField } from '@/ui/form/controls/NumberField'

const PREDEFINED_PACKAGES = [
  PredefinedPackage.None,
  PredefinedPackage.WebLite1,
  PredefinedPackage.Web5,
  PredefinedPackage.Web20,
  PredefinedPackage.Web50,
  PredefinedPackage.Web100,
  PredefinedPackage.Web250,
  PredefinedPackage.WebPro,
]

type FormState = {
  accountingOnly: boolean
  isEwiSystem: boolean
  companyType: CompanyType
  existingDatabase: boolean
  server: ServerModel | null
  servers: ServerModel[]
  database: { id: string; name: string } | null
  wpfCompanies: CompanyConfigurationModel[]
  selectedWpfCompanies: CompanyConfigurationModel[]
  uniqueIdentifier: string
  companyName: string
  nip: string
  krs: string
  regon: string
  email: string
  notificationsEmail: string
  contactEmail: string
  contactPhone: string
  licenseNumber: string
  street: string
  houseNumber: string
  buildingNumber: string
  postalCode: string
  city: string
  useLocalImport: number
  predefinedPackageSelected: boolean
  predefinedPackage: PredefinedPackage
  packageMultiplier: number
  activeDriversLimits: number | null
  userRoles: UserRole[]
  userId: string | null
  role: RolePermissionModel | null
  newCompanyErrorMessage: string | null
}

const INITIAL: FormState = {
  accountingOnly: false,
  isEwiSystem: false,
  companyType: CompanyType.NORMAL,
  existingDatabase: false,
  server: null,
  servers: [],
  database: null,
  wpfCompanies: [],
  selectedWpfCompanies: [],
  uniqueIdentifier: '',
  companyName: '',
  nip: '',
  krs: '',
  regon: '',
  email: '',
  notificationsEmail: '',
  contactEmail: '',
  contactPhone: '',
  licenseNumber: '',
  street: '',
  houseNumber: '',
  buildingNumber: '',
  postalCode: '',
  city: '',
  useLocalImport: 2,
  predefinedPackageSelected: false,
  predefinedPackage: PredefinedPackage.None,
  packageMultiplier: 1,
  activeDriversLimits: null,
  userRoles: [],
  userId: null,
  role: null,
  newCompanyErrorMessage: null,
}

type FieldValidation = { isInvalid: boolean; message: string }
type Validation = Record<string, FieldValidation> & { isValid: boolean }

const isEmptyText = (value: string) => value == null || value.trim() === ''
const isNipValid = (nip: string) => (isEmptyText(nip) ? true : validateNip(nip))
const isEmailValid = (email: string) =>
  isEmptyText(email) ? true : validateEmail(email)

/**
 * Reguły przepisane 1:1 z `getValidationRules()` starej zakładki - łącznie z tym,
 * że firma serwisowa nie wymaga pojedynczego serwera, a firma z istniejącą bazą
 * wymaga wskazania baz WPF.
 */
export function validateAddCompany(
  state: FormState,
  t: (key: string) => string,
): Validation {
  const fields = [
    'companyName',
    'uniqueIdentifier',
    'nip',
    'role',
    'email',
    'notificationsEmail',
    'server',
    'servers',
    'selectedWpfCompanies',
  ]
  const validation = { isValid: true } as Validation
  fields.forEach((field) => {
    validation[field] = { isInvalid: false, message: '' }
  })

  const fail = (field: string, message: string) => {
    validation[field] = { isInvalid: true, message: t(message) }
    validation.isValid = false
  }

  const isService = state.companyType === CompanyType.SERVICE

  if (isEmptyText(state.companyName)) {
    fail('companyName', 'administrationPanel-companies.validation-companyName')
  }
  if (isEmptyText(state.uniqueIdentifier)) {
    fail(
      'uniqueIdentifier',
      'administrationPanel-companies.validation-uniqueIdentifier',
    )
  }
  if (!isNipValid(state.nip)) {
    fail('nip', 'administrationPanel-companies.validation-nip-invalid')
  }
  if (state.userId != null && state.role == null) {
    fail('role', 'administrationPanel-companies.validation-role')
  }
  if (!isEmailValid(state.email)) {
    fail('email', 'administrationPanel-companies.validation-email-invalid')
  }
  if (!isEmailValid(state.notificationsEmail)) {
    fail(
      'notificationsEmail',
      'administrationPanel-companies.validation-email-invalid',
    )
  }
  if (
    !state.accountingOnly &&
    !(state.server != null || (isAdmin() && isService))
  ) {
    fail('server', 'administrationPanel-companies.validation-server')
  }
  if (
    !state.accountingOnly &&
    isService &&
    (state.servers == null || state.servers.length === 0)
  ) {
    fail('servers', 'administrationPanel-companies.validation-server')
  }
  if (
    state.existingDatabase &&
    state.server != null &&
    !isService &&
    (state.selectedWpfCompanies == null ||
      state.selectedWpfCompanies.length === 0)
  ) {
    fail(
      'selectedWpfCompanies',
      'administrationPanel-companies.validation-database',
    )
  }

  return validation
}

type Props = {
  /** Firma serwisowa, w której kontekście otwarty jest panel (może być pusta). */
  currentCompanyId?: string
  currentCompany?: CompanyDetailsModel
  /** Firmy już widoczne na liście - podpowiedzi „ta baza jest już użyta". */
  existingCompanies: CompanyDetailsModel[]
  onAdded: () => void | Promise<void>
}

/**
 * Formularz dodawania firmy - funkcyjny odpowiednik `renderContent()` starej zakładki.
 *
 * Serwery, role i użytkownicy idą z react-query (te same klucze co reszta panelu),
 * a nie z propsów wstrzykiwanych przez HOC-i.
 */
export const AddCompanyForm: React.FC<Props> = ({
  currentCompanyId,
  currentCompany,
  existingCompanies,
  onAdded,
}) => {
  const { t } = useLocalize()
  const confirm = useConfirm()
  const queryClient = useQueryClient()

  const [state, setState] = React.useState<FormState>(INITIAL)
  const [submitted, setSubmitted] = React.useState(false)

  const update = React.useCallback(
    (patch: Partial<FormState>) => setState((prev) => ({ ...prev, ...patch })),
    [],
  )

  const { data: servers = [] } = useQuery({
    queryKey: serversKey(currentCompanyId ?? null),
    queryFn: () => fetchServers(currentCompanyId ?? null),
  })
  const { data: roles = [] } = useQuery({
    queryKey: ROLES_KEY,
    queryFn: fetchRoles,
  })
  const { data: users = [] } = useQuery({
    queryKey: usersKey(currentCompanyId ?? null),
    queryFn: () => fetchUsers(currentCompanyId ?? undefined),
  })

  // Stara zakładka budowała `userRoles` w `componentWillReceiveProps`, gdy przyszła
  // nowa lista użytkowników; tutaj to samo, tylko wyzwalane zmianą wyniku zapytania.
  React.useEffect(() => {
    setState((prev) => ({
      ...prev,
      userRoles: (users as UserModel[]).map((user) => ({ user, roles: [] })),
    }))
  }, [users])

  // Nowa firma domyślnie trafia na serwer domyślny; admin może wybrać inny.
  const defaultServer = (servers as ServerModel[]).find(
    (server) => server.isDefault,
  )
  React.useEffect(() => {
    if (
      defaultServer &&
      state.server == null &&
      state.companyType === CompanyType.NORMAL &&
      !state.existingDatabase &&
      !state.accountingOnly
    )
      update({ server: defaultServer })
  }, [
    defaultServer,
    state.server,
    state.companyType,
    state.existingDatabase,
    state.accountingOnly,
    update,
  ])

  const validation = validateAddCompany(state, t)
  const showError = (field: string) =>
    submitted && validation[field]?.isInvalid ? validation[field].message : null

  const errorContains = (code: string) =>
    state.newCompanyErrorMessage != null &&
    state.newCompanyErrorMessage.includes(code)

  const populateWpfCompanies = async (serverId: string) => {
    const result = await getWpfCompanies(currentCompanyId, serverId)
    if (result.error != null) {
      update({ newCompanyErrorMessage: `errorCode.${result.error}` })
    } else {
      update({
        wpfCompanies: Array.isArray(result) ? result : [],
        newCompanyErrorMessage: null,
      })
    }
  }

  const chooseServer = async (element: ServerModel) => {
    update({
      server: element,
      database: null,
      servers: [],
      selectedWpfCompanies: [],
      wpfCompanies: [],
    })

    if (state.existingDatabase) {
      await populateWpfCompanies(element.id)
      update({ companyName: '' })
    }
  }

  const chooseServers = (selected: ServerModel[]) =>
    update({
      servers: selected,
      database: null,
      server: null,
      selectedWpfCompanies: [],
      wpfCompanies: [],
    })

  const chooseWpfCompanies = (companies: CompanyConfigurationModel[]) =>
    update({
      newCompanyErrorMessage: null,
      selectedWpfCompanies: companies,
      uniqueIdentifier:
        companies.length >= 1 ? companies[0].uniqueIdentifier : '',
      companyName:
        companies.length === 1
          ? companies[0].name
          : companies.length > 1
            ? t('administrationPanel-companies.name-set-automatically')
            : '',
    })

  const [pickedUserId, setPickedUserId] = React.useState<string | null>(null)
  const pickedEntry = state.userRoles.find(
    (entry) => String(entry.user.id) === pickedUserId,
  )

  const chooseCompany = (user: UserModel, role: RolePermissionModel) =>
    setState((prev) => ({
      ...prev,
      userRoles: prev.userRoles.map((entry) =>
        entry.user.id === user.id
          ? { ...entry, roles: [...entry.roles, role] }
          : entry,
      ),
    }))

  const removeRole = (id: string, role: RolePermissionModel) =>
    setState((prev) => ({
      ...prev,
      userRoles: prev.userRoles.map((entry) =>
        entry.user.id === id
          ? { ...entry, roles: entry.roles.filter((r) => r.id !== role.id) }
          : entry,
      ),
    }))

  const fillFromGus = async () => {
    if (isEmptyText(state.nip)) return
    setSubmitted(true)
    if (!isNipValid(state.nip)) return

    const result = await importFromGus(state.nip)
    if (!result) return

    if (result.name) {
      toast.success({
        title: t('administrationPanel-companies.imported-from-gus'),
        text: result.name,
      })
      update({
        nip: result.nip,
        regon: result.regon,
        krs: result.krs,
        buildingNumber: result.buildingNumber,
        houseNumber: result.flatNumber,
        companyName: result.name,
        postalCode: result.postalCode,
        city: result.city,
        street: result.street,
        contactEmail: result.email,
        contactPhone: result.phone,
      })
    } else {
      toast.warning({
        title: t('administrationPanel-companies.not-imported-from-gus'),
        text: t('administrationPanel-companies.not-imported-from-gus-details'),
      })
      update({
        nip: '',
        regon: '',
        krs: '',
        buildingNumber: '',
        houseNumber: '',
        companyName: '',
        postalCode: '',
        city: '',
        street: '',
        contactEmail: '',
        contactPhone: '',
      })
    }
  }

  const submitCompany = async () => {
    const databaseId =
      state.companyType === CompanyType.SERVICE
        ? null
        : state.database != null
          ? state.database.id
          : currentCompany && currentCompany.databaseId

    const companyToBeAdded: CreateCompanyModel = {
      databaseId,
      nip: state.nip,
      krs: state.krs,
      regon: state.regon,
      email: state.email,
      notificationsEmail: state.notificationsEmail,
      companyName: state.companyName,
      contactEmail: state.contactEmail,
      contactPhone: state.contactPhone,
      licenseNumber: state.licenseNumber,
      street: state.street,
      houseNumber: state.houseNumber,
      buildingNumber: state.buildingNumber,
      postalCode: state.postalCode,
      city: state.city,
      isServiceCompany: isAdmin()
        ? state.companyType === CompanyType.SERVICE
        : false,
      uniqueIdentifier: state.uniqueIdentifier,
      legacy: true,
      accountingOnly: state.accountingOnly,
      isEwiSystem: state.isEwiSystem,
      useLocalImport: state.useLocalImport,
      activeDriversLimits: !state.predefinedPackageSelected
        ? state.activeDriversLimits
        : null,
      package: state.predefinedPackageSelected ? state.predefinedPackage : null,
      packageMultiplier: state.packageMultiplier ? state.packageMultiplier : 1,
      servers: state.accountingOnly
        ? null
        : state.companyType === CompanyType.SERVICE
          ? state.servers.map((server) => server.id)
          : [state.server.id],
      wpfCompanies:
        state.companyType === CompanyType.NORMAL && state.existingDatabase
          ? state.selectedWpfCompanies
          : null,
    }
    companyToBeAdded.tenantId = currentCompanyId

    const userRoles: UserRoleModel[] = state.userRoles
      .filter((entry) => entry.roles.length > 0)
      .map((entry) => ({
        userId: entry.user.id,
        roles: entry.roles.map((role) => role.id),
      }))
    if (userRoles.length > 0) {
      companyToBeAdded.userRoles = userRoles
    }

    const details =
      state.selectedWpfCompanies != null && state.selectedWpfCompanies.length > 0
        ? state.selectedWpfCompanies
            .map((company) => company.name)
            .filter((name, index, all) => all.indexOf(name) === index)
            .join(', ')
        : state.companyName

    const newCompany = await addCompany(companyToBeAdded)
    if (newCompany == null || newCompany.error == null) {
      toast.success({
        title: t('administrationPanel-companies.company-added'),
        text: details,
      })

      // Bez admina lista firm decyduje o uprawnieniach zapisanych lokalnie.
      if (!isAdmin()) {
        const userInfo = await getUserInfo()
        const freshRoles = await queryClient.fetchQuery({
          queryKey: ROLES_KEY,
          queryFn: fetchRoles,
        })
        saveUserInfo(userInfo, freshRoles as RolePermissionModel[], [])
      }

      if (state.existingDatabase && state.server != null) {
        await populateWpfCompanies(state.server.id)
      }

      setSubmitted(false)
      setState((prev) => ({
        ...INITIAL,
        companyType: prev.companyType,
        userRoles: prev.userRoles.map((entry) => ({ ...entry, roles: [] })),
      }))
      await onAdded()
    } else {
      update({ newCompanyErrorMessage: `errorCode.${newCompany.error}` })
      toast.error({
        title: t('administrationPanel-companies.company-not-added'),
        text: details,
      })
    }
  }

  const handleSubmit = async () => {
    setSubmitted(true)
    if (!validateAddCompany(state, t).isValid) return

    // Firma zwykła zakłada bazę, więc dostaje potwierdzenie; serwisowa nie.
    if (state.companyType === CompanyType.NORMAL) {
      const names =
        state.selectedWpfCompanies.length > 1
          ? state.selectedWpfCompanies
              .map((company) => company.name)
              .filter((name, index, all) => all.indexOf(name) === index)
              .join(', ')
          : state.companyName

      const confirmed = await confirm({
        title: t('addCompanyConfirmationPopup.title'),
        message: [
          t('addCompanyConfirmationPopup.warning'),
          t('addCompanyConfirmationPopup.warning-1'),
          `${t('addCompanyConfirmationPopup.warning-2')} ${names} ${t(
            'addCompanyConfirmationPopup.warning-3',
          )}`,
        ].join('\n\n'),
        confirmText: t('answer.yes'),
        cancelText: t('answer.no'),
      })
      if (!confirmed) return
    }

    await submitCompany()
  }

  const isService = state.companyType === CompanyType.SERVICE

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-6">
        {isAdmin() && (
          <>
            <CheckboxField
              id="normalCompany"
              checked={state.companyType === CompanyType.NORMAL}
              label={t('administrationPanel-companies.company-type-normal')}
              onChange={() =>
                update({ companyType: CompanyType.NORMAL, servers: [] })
              }
            />
            <CheckboxField
              id="serviceCompany"
              checked={isService}
              label={t('administrationPanel-companies.company-type-service')}
              onChange={() =>
                update({
                  companyType: CompanyType.SERVICE,
                  server: null,
                  database: null,
                  selectedWpfCompanies: [],
                  wpfCompanies: [],
                })
              }
            />
          </>
        )}
        {!isService && !state.accountingOnly && (
          <CheckboxField
            id="existingDatabase"
            checked={state.existingDatabase}
            label={t('administrationPanel-companies.add-based-on-database')}
            onChange={() =>
              update({
                existingDatabase: !state.existingDatabase,
                server: null,
                database: null,
                servers: [],
                newCompanyErrorMessage: null,
              })
            }
          />
        )}
        {isAdmin() && (
          <Button
            variant="primary"
            mode="outline"
            size="sm"
            disabled={isEmptyText(state.nip)}
            onClick={fillFromGus}
          >
            {t('administrationPanel-companies.fill-from-gus')}
          </Button>
        )}
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3 xl:tw-grid-cols-4">
        {(isService || !state.existingDatabase) && (
          <TextField
            fullWidth
            label={t('administrationPanel-companies.unique-identifier')}
            value={state.uniqueIdentifier}
            onChange={(event) =>
              update({
                uniqueIdentifier: event.target.value,
                newCompanyErrorMessage: null,
              })
            }
            error={
              showError('uniqueIdentifier') ??
              (errorContains('COMPANY_UNIQUE_EXISTS') ||
              errorContains('DATABASE_EXISTS')
                ? t(state.newCompanyErrorMessage)
                : null)
            }
          />
        )}

        {isAdmin() && isService && !state.accountingOnly && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
              {t('administrationPanel-companies.servers')}
            </span>
            <Dropdown
              multiple
              searchPlaceholder={t('administrationPanel-companies.find-server')}
              placeholder={t('administrationPanel-companies.choose-server')}
              options={(servers as ServerModel[]).map((server) => {
                const taken =
                  server.inUse ||
                  existingCompanies.some(
                    (company) =>
                      String(company.uniqueIdentifier) === String(server.id),
                  )
                return {
                  value: String(server.id),
                  label: server.name,
                  disabled: taken,
                  title: taken
                    ? t('administrationPanel-companies.existing-server-title')
                    : server.name,
                }
              })}
              value={(state.servers ?? []).map((server) => String(server.id))}
              onChange={(ids: string[]) =>
                chooseServers(
                  (servers as ServerModel[]).filter((server) =>
                    ids.includes(String(server.id)),
                  ),
                )
              }
            />
            {(showError('servers') ||
              errorContains('SERVER_ALREADY_ASSIGNED')) && (
              <span className="tw-text-xs tw-text-red-500">
                {showError('servers') ?? t(state.newCompanyErrorMessage)}
              </span>
            )}
          </div>
        )}

        {!isService && !state.accountingOnly && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
              {t('administrationPanel-companies.server')}
            </span>
            <Dropdown
              searchPlaceholder={t('administrationPanel-companies.find-server')}
              placeholder={t('administrationPanel-companies.choose-server')}
              options={(servers as ServerModel[]).map((server) => ({
                value: String(server.id),
                label: server.isDefault
                  ? `${server.name} (${t('administrationPanel-servers.default-server').toLowerCase()})`
                  : server.name,
                // Serwer bez połączenia zostaje widoczny, ale nie da się go wybrać.
                disabled: server.status === ConnectionStatus.NOT_CONNECTED,
                title:
                  server.status === ConnectionStatus.NOT_CONNECTED
                    ? t('administrationPanel-companies.invalid-server-info')
                    : server.name,
              }))}
              value={state.server ? String(state.server.id) : null}
              onChange={(id: string) => {
                const found = (servers as ServerModel[]).find(
                  (server) => String(server.id) === id,
                )
                if (found) chooseServer(found)
              }}
            />
            {(showError('server') ||
              errorContains('SERVER_ALREADY_ASSIGNED') ||
              errorContains('ADMINISTRATION_DATABASE_NOT_AVAILABLE')) && (
              <span className="tw-text-xs tw-text-red-500">
                {showError('server') ?? t(state.newCompanyErrorMessage)}
              </span>
            )}
          </div>
        )}

        {!isService && state.existingDatabase && !state.accountingOnly && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
              {t('administrationPanel-companies.database')}
            </span>
            <Dropdown
              multiple
              searchPlaceholder={t('administrationPanel-companies.find-database')}
              placeholder={t('administrationPanel-companies.choose-database')}
              options={state.wpfCompanies.map((company) => {
                const taken =
                  company.inUse ||
                  existingCompanies
                    .filter(
                      (existing) =>
                        state.server == null ||
                        String(existing.serverId) === String(state.server.id),
                    )
                    .some(
                      (existing) =>
                        String(existing.uniqueIdentifier) ===
                        String(company.uniqueIdentifier),
                    )
                return {
                  value: String(company.uniqueIdentifier),
                  label: company.name,
                  disabled: taken,
                  title: taken
                    ? t('administrationPanel-companies.existing-company-title')
                    : company.name,
                }
              })}
              value={(state.selectedWpfCompanies ?? []).map((company) =>
                String(company.uniqueIdentifier),
              )}
              onChange={(ids: string[]) =>
                chooseWpfCompanies(
                  state.wpfCompanies.filter((company) =>
                    ids.includes(String(company.uniqueIdentifier)),
                  ),
                )
              }
            />
            {(showError('selectedWpfCompanies') ||
              errorContains('COMPANY_UNIQUE_EXISTS') ||
              errorContains('DATABASE_EXISTS') ||
              errorContains('LEGACY_DATABASE_IN_USE')) && (
              <span className="tw-text-xs tw-text-red-500">
                {showError('selectedWpfCompanies') ??
                  t(state.newCompanyErrorMessage)}
              </span>
            )}
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-gap-1">
          <span className="tw-text-sm tw-font-medium tw-text-gray-700">
            {t('administrationPanel-companies.user')}
          </span>
          {/* Stary `MultiselectUserDropdown` łączył wybór użytkownika z przypisaniem
              roli w bootstrapowym `Popover`. Tutaj to samo, ale z komponentów, których
              używa już reszta panelu: wspólny `Dropdown` + `RolePicker`. */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <Dropdown
              className="tw-flex-1"
              searchPlaceholder={t('administrationPanel-companies.find-user')}
              placeholder={t('administrationPanel-companies.choose-user')}
              options={state.userRoles.map((entry) => ({
                value: String(entry.user.id),
                label: `${entry.user.firstName || '-'} ${entry.user.surname || '-'}, ${entry.user.login}`,
                content: (
                  <span className="tw-flex tw-flex-col">
                    <span className="tw-text-primary">{entry.user.login}</span>
                    <span>{`${entry.user.firstName || '-'} ${entry.user.surname || '-'}`}</span>
                  </span>
                ),
              }))}
              value={pickedUserId}
              onChange={setPickedUserId}
            />
            {pickedEntry && (
              <RolePicker
                available={roles as RolePermissionModel[]}
                selected={pickedEntry.roles}
                onToggle={(role) =>
                  pickedEntry.roles.some((assigned) => assigned.id === role.id)
                    ? removeRole(String(pickedEntry.user.id), role)
                    : chooseCompany(pickedEntry.user, role)
                }
              />
            )}
          </div>

          {/* Podgląd tego, co już przypisano - wcześniej widoczne tylko po rozwinięciu. */}
          {state.userRoles.some((entry) => entry.roles.length > 0) && (
            <ul className="tw-mt-1 tw-flex tw-flex-col tw-gap-1">
              {state.userRoles
                .filter((entry) => entry.roles.length > 0)
                .map((entry) => (
                  <li
                    key={String(entry.user.id)}
                    className="tw-flex tw-items-center tw-gap-2 tw-text-sm"
                  >
                    <span className="tw-min-w-0 tw-truncate">
                      {entry.user.login}
                    </span>
                    <RolePicker
                      available={roles as RolePermissionModel[]}
                      selected={entry.roles}
                      onToggle={(role) =>
                        entry.roles.some((assigned) => assigned.id === role.id)
                          ? removeRole(String(entry.user.id), role)
                          : chooseCompany(entry.user, role)
                      }
                    />
                  </li>
                ))}
            </ul>
          )}
        </div>
      </div>

      <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3 xl:tw-grid-cols-4">
        <TextField
          fullWidth
          label={t('administrationPanel-companies.company-name')}
          readOnly={state.selectedWpfCompanies.length > 1}
          value={state.companyName}
          onChange={(event) => update({ companyName: event.target.value })}
          error={showError('companyName')}
        />
        <TextField
          fullWidth
          label={t('administrationPanel-companies.nip')}
          value={state.nip}
          onChange={(event) => update({ nip: event.target.value })}
          error={showError('nip')}
        />
        {!state.accountingOnly && (
          <TextField
            fullWidth
            label={t('administrationPanel-companies.email')}
            value={state.email}
            onChange={(event) => update({ email: event.target.value })}
            error={showError('email')}
          />
        )}
        {!state.accountingOnly && (
          <TextField
            fullWidth
            label={t('administrationPanel-companies.notifications-email')}
            value={state.notificationsEmail}
            onChange={(event) =>
              update({ notificationsEmail: event.target.value })
            }
            error={showError('notificationsEmail')}
          />
        )}

        {isAdmin() && !state.accountingOnly && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-sm tw-font-medium tw-text-gray-700">
              {t('administrationPanel-companies.remote-import')}
            </span>
            <Dropdown
              compact
              placeholder={t('administrationPanel-companies.remote-import')}
              options={ImportTypes.map((importType) => ({
                value: String(importType.key),
                label: t(
                  `administrationPanel-companies.remote-import-${importType.type}`,
                ),
              }))}
              value={String(state.useLocalImport)}
              onChange={(value) => update({ useLocalImport: Number(value) })}
            />
          </div>
        )}

        {isAdmin() && !state.accountingOnly && (
          <div className="tw-flex tw-flex-col tw-gap-1">
            <CheckboxField
              id="predefinedPackage"
              checked={state.predefinedPackageSelected}
              label={t('administrationPanel-companies.predefinedPackage')}
              onChange={() =>
                update({
                  predefinedPackageSelected: !state.predefinedPackageSelected,
                })
              }
            />
            {!state.predefinedPackageSelected ? (
              <NumberField
                label={t('administrationPanel-companies.package')}
                value={state.activeDriversLimits ?? 0}
                setValue={(value) =>
                  update({
                    activeDriversLimits: value ?? 0,
                    predefinedPackage: null,
                  })
                }
              />
            ) : (
              <div className="tw-flex tw-items-end tw-gap-2">
                <Dropdown
                  compact
                  value={String(state.predefinedPackage ?? PredefinedPackage.None)}
                  options={PREDEFINED_PACKAGES.map((predefined) => ({
                    value: String(predefined),
                    label: getPredefinedPackageName(predefined, t),
                  }))}
                  onChange={(value) =>
                    update({
                      predefinedPackage: Number(value) as PredefinedPackage,
                      activeDriversLimits: null,
                    })
                  }
                />
                <span className="tw-pb-2">x</span>
                <div className="tw-w-16">
                  <NumberField
                    value={state.packageMultiplier ?? 1}
                    setValue={(value) =>
                      update({
                        packageMultiplier: value ?? 1,
                        activeDriversLimits: null,
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="tw-flex tw-justify-end">
        <Button variant="primary" size="sm" onClick={handleSubmit}>
          {t('operation.add')}
        </Button>
      </div>

    </div>
  )
}
